import React, { useState } from 'react';

const ReferralModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(''); // 'success', 'info', 'error'
  const [showAlreadyMemberPrompt, setShowAlreadyMemberPrompt] = useState(false);
  const [showSuccessConfirmation, setShowSuccessConfirmation] = useState(false);
  const [referredPersonName, setReferredPersonName] = useState('');

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Create FormData object since backend expects Form data
      const formDataToSend = new FormData();
      formDataToSend.append('referral_first_name', formData.firstName);
      formDataToSend.append('referral_last_name', formData.lastName);
      formDataToSend.append('referral_email', formData.email);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rewards/send-referral`,
        {
          method: 'POST',
          credentials: 'include',
          body: formDataToSend
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send referral');
      }

      // Check if user is already a member
      if (data.already_member) {
        setMessageType('info');
        setMessage(data.message);
        setShowAlreadyMemberPrompt(true); // Show "refer someone else?" prompt
      } else {
        // Show success confirmation modal instead of auto-closing
        setReferredPersonName(data.referral_name);
        setShowSuccessConfirmation(true);
      }

    } catch (error) {
      console.error('Referral error:', error);
      setMessageType('error');
      setMessage(error.message || 'Failed to send referral. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '', email: '' });
    setMessage(null);
    setMessageType('');
    setShowAlreadyMemberPrompt(false);
    setShowSuccessConfirmation(false);
    setReferredPersonName('');
    onClose();
  };

  const handleReferSomeoneElse = () => {
    // Reset form and show referral form again
    setFormData({ firstName: '', lastName: '', email: '' });
    setMessage(null);
    setMessageType('');
    setShowAlreadyMemberPrompt(false);
  };

  const handleNoMoreReferrals = () => {
    // Close modal completely
    handleClose();
  };

  const handleSuccessOk = () => {
    // Reset all internal state
    setShowSuccessConfirmation(false);
    setFormData({ firstName: '', lastName: '', email: '' });
    setMessage(null);
    setMessageType('');
    setReferredPersonName('');
    // Close modal via parent
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 max-w-full sm:max-w-md w-full mx-4 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-4 sm:mb-6">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">
              🎁 Refer a Friend
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Share Digital Ink AI and earn rewards!
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Success Confirmation View */}
        {showSuccessConfirmation && (
          <div className="space-y-3 sm:space-y-4">
            {/* Success Icon & Message */}
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🎉</span>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-2">
                Referral Sent Successfully!
              </h4>
              <p className="text-slate-600">
                Your invitation has been sent to <strong>{referredPersonName}</strong>
              </p>
            </div>

            {/* Important Information Box */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-5 space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💰</span>
                <div>
                  <p className="font-semibold text-blue-900 mb-1">When Will You Earn Points?</p>
                  <p className="text-xs sm:text-sm text-blue-800">
                    You'll receive <strong>200 reward points</strong> when {(() => {
                      const parts = referredPersonName.trim().split(' ');
                      if (parts.length === 1) return parts[0];
                      const firstPart = parts[0];
                      const isInitial = firstPart.length <= 2 && (firstPart.length === 1 || firstPart.endsWith('.'));
                      if (isInitial && parts.length > 1) return `${parts[0]} ${parts[1]}`;
                      return firstPart;
                    })()} registers 
                    and becomes a <strong>paid subscriber</strong> (not just a free account).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-2xl">📧</span>
                <div>
                  <p className="font-semibold text-blue-900 mb-1">What Happens Next?</p>
                  <p className="text-xs sm:text-sm text-blue-800">
                    {referredPersonName} will receive an invitation email with a link to join Digital Ink AI. 
                    We'll notify you once they complete their subscription!
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSuccessOk}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all"
              >
                Got It!
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSuccessConfirmation(false);
                  setFormData({ firstName: '', lastName: '', email: '' });
                  setReferredPersonName('');
                }}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-6 rounded-lg transition-all"
              >
                Refer Another Friend
              </button>
            </div>
          </div>
        )}

        {/* Rewards Info - Only show in form view */}
        {!showSuccessConfirmation && !showAlreadyMemberPrompt && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-slate-700">
              <strong>Earn 200 points</strong> when your friend becomes a paid subscriber!
            </p>
          </div>
        )}

        {/* Message Display */}
        {message && !showAlreadyMemberPrompt && !showSuccessConfirmation && (
          <div
            className={`rounded-lg p-4 mb-4 ${
              messageType === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : messageType === 'info'
                ? 'bg-blue-50 border border-blue-200 text-blue-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <p className="text-sm">{message}</p>
          </div>
        )}

        {/* Already Member Prompt */}
        {showAlreadyMemberPrompt && !showSuccessConfirmation && (
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs sm:text-sm text-blue-800 mb-2">{message}</p>
            </div>
            
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-center text-slate-700 font-medium mb-4">
                Would you like to refer someone else?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleReferSomeoneElse}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all"
                >
                  👥 Yes, Refer Another Friend
                </button>
                <button
                  onClick={handleNoMoreReferrals}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-6 rounded-lg transition-all"
                >
                  No Thanks
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form - Hidden when showing already member prompt or success confirmation */}
        {!showAlreadyMemberPrompt && !showSuccessConfirmation && (
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="John"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Doe"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="john@example.com"
              disabled={loading}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                '📧 Send Referral'
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
        )}

        {/* Footer Note - Only show in form view */}
        {!showAlreadyMemberPrompt && !showSuccessConfirmation && (
          <p className="text-xs text-center text-slate-500 mt-4">
            Points are awarded when your friend becomes a paid subscriber
          </p>
        )}
      </div>
    </div>
  );
};

export default ReferralModal;
